import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import axios, { OrchestratorAPI } from "../api/axios";
import {
  filterActiveOrchestrations,
  GatingOperationMode,
  InwardContext,
  NO_INWARD_CONTEXT,
  resolveActiveOperationMode,
  resolveInwardContext,
} from "../utils/inwardOrchestration";

const JSON_HEADERS = { Accept: "application/json", tenantId: 1 };

export interface InwardStatus extends InwardContext {
  /** True until the first orchestration read resolves. */
  loading: boolean;
  /**
   * Operation mode of the active orchestration, if any. Read from the same poll as the inward
   * context so the header nav gating and this screen agree on what the warehouse is doing.
   */
  activeOperationMode?: GatingOperationMode;
  refresh: () => Promise<InwardContext>;
}

const DEFAULT_INWARD_STATUS: InwardStatus = {
  ...NO_INWARD_CONTEXT,
  loading: true,
  refresh: async () => NO_INWARD_CONTEXT,
};

/**
 * Polls the orchestrator once for the whole app. App owns the single instance and publishes it
 * through {@link InwardStatusContext} so the header tab gating and the Material Inward screen
 * always read the same answer.
 */
export function useInwardStatus(pollMs = 5000): InwardStatus {
  const [context, setContext] = useState<InwardContext>(NO_INWARD_CONTEXT);
  const [activeOperationMode, setActiveOperationMode] = useState<
    GatingOperationMode | undefined
  >(undefined);
  const [loading, setLoading] = useState(true);

  const contextRef = useRef<InwardContext>(NO_INWARD_CONTEXT);
  const stationsRef = useRef<unknown>(null);
  const stationsKeyRef = useRef<string | null>(null);
  const readInFlightRef = useRef(false);

  const readContext = useCallback(async (): Promise<InwardContext> => {
    try {
      const res = await OrchestratorAPI.get("orchestrations", { headers: JSON_HEADERS });

      // Only a confirmed response may change the answer. A transient failure keeps the last
      // known context rather than dropping the operator's slots or the nav lock for 5s.
      if (res.status !== 200 || !Array.isArray(res.data)) {
        return contextRef.current;
      }

      const nowMs = Date.now();

      // Station purposes only change between runs, so they are re-read when the set of active
      // orchestrations changes rather than on every poll.
      const activeKey = filterActiveOrchestrations(res.data, nowMs)
        .map((orchestration) => orchestration.id ?? "")
        .sort()
        .join("|");

      if (stationsRef.current === null || activeKey !== stationsKeyRef.current) {
        try {
          const stationsRes = await axios.get("PickStation/inwardStations", {
            headers: JSON_HEADERS,
          });
          if (stationsRes.status === 200 && Array.isArray(stationsRes.data)) {
            stationsRef.current = stationsRes.data;
            stationsKeyRef.current = activeKey;
          }
        } catch (error) {
          console.error("Failed to fetch inward stations for inward status:", error);
        }
      }

      const next = resolveInwardContext(res.data, stationsRef.current, nowMs);
      contextRef.current = next;
      setContext(next);
      setActiveOperationMode(resolveActiveOperationMode(res.data, nowMs));
      return next;
    } catch (error) {
      console.error("Failed to resolve inward status:", error);
      return contextRef.current;
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback((): Promise<InwardContext> => readContext(), [readContext]);

  useEffect(() => {
    const tick = async () => {
      if (readInFlightRef.current) return;

      readInFlightRef.current = true;
      try {
        await readContext();
      } finally {
        readInFlightRef.current = false;
      }
    };

    tick();
    const interval = setInterval(tick, pollMs);

    return () => {
      clearInterval(interval);
    };
  }, [pollMs, readContext]);

  return { ...context, loading, activeOperationMode, refresh };
}

export const InwardStatusContext = createContext<InwardStatus>(DEFAULT_INWARD_STATUS);

export function useInwardStatusContext(): InwardStatus {
  return useContext(InwardStatusContext);
}
